import { useRef, useState, type DragEvent } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { SELECTABLE_IMAGE_TYPES } from '@/lib/merchantImages';

interface ImageDropZoneProps {
  /** Names the file input, for assistive tech and tests. */
  readonly label: string;
  readonly prompt: string;
  readonly buttonLabel: string;
  readonly multiple: boolean;
  readonly disabled: boolean;
  readonly onFiles: (files: File[]) => void;
}

/**
 * Where an operator drops image files, or picks them. The picker offers only
 * the accepted types; a drop can carry anything, and the upload hook refuses
 * what is not an image with copy beside the zone.
 */
export function ImageDropZone({ label, prompt, buttonLabel, multiple, disabled, onFiles }: ImageDropZoneProps) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) onFiles(files);
  };

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      data-testid={`${label} drop zone`}
      sx={{
        px: 2,
        py: 3,
        borderRadius: `${borderRadius.md}px`,
        border: `2px dashed ${dragging ? webColors.state.info : webColors.border.medium}`,
        bgcolor: dragging ? webColors.state.infoBg : webColors.surface.primary,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Stack spacing={1} alignItems="center">
        <Icon name="image" size={24} color={webColors.text.tertiary} />
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {prompt}
        </Typography>
        <Button variant="secondary" size="sm" disabled={disabled} onClick={() => input.current?.click()}>
          {buttonLabel}
        </Button>
        <input
          ref={input}
          type="file"
          hidden
          aria-label={label}
          accept={SELECTABLE_IMAGE_TYPES.join(',')}
          multiple={multiple}
          disabled={disabled}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            // Cleared so picking the same file again (after a failure) fires again.
            event.target.value = '';
            if (files.length > 0) onFiles(files);
          }}
        />
      </Stack>
    </Box>
  );
}
